import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Pagination,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { useNavigate } from 'react-router-dom';
import ProjectList from '../components/ProjectList';
import EmptyState from '../components/EmptyState';
import useProjectStore from '../store/useProjectStore';

const PAGE_SIZE = 6;

const StudentProjectDashboard = () => {
  const navigate = useNavigate();
  const myProjects = useProjectStore((s) => s.myProjects);
  const loadingMyProjects = useProjectStore((s) => s.loadingMyProjects);
  const errorMyProjects = useProjectStore((s) => s.errorMyProjects);
  const fetchMyProjects = useProjectStore((s) => s.fetchMyProjects);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchMyProjects();
  }, [fetchMyProjects]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const searchFiltered = useMemo(() => {
    const raw = Array.isArray(myProjects) ? myProjects : [];
    const term = search.trim().toLowerCase();
    if (!term) return raw;
    return raw.filter((p) => {
      const title = (p.title || '').toLowerCase();
      const idStr = String(
        p.student?.id ?? p.studentId?._id ?? p.studentId ?? '',
      ).toLowerCase();
      return title.includes(term) || idStr.includes(term);
    });
  }, [myProjects, search]);

  const sortedProjects = useMemo(() => {
    return [...searchFiltered].sort((a, b) => {
      const tA = a.updatedAt != null ? new Date(a.updatedAt).getTime() : 0;
      const tB = b.updatedAt != null ? new Date(b.updatedAt).getTime() : 0;
      if (tB !== tA) return tB - tA;
      const idA = String(a.student?.id ?? a.studentId?._id ?? a.studentId ?? '');
      const idB = String(b.student?.id ?? b.studentId?._id ?? b.studentId ?? '');
      return idA.localeCompare(idB, undefined, { numeric: true });
    });
  }, [searchFiltered]);

  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / PAGE_SIZE) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedProjects = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedProjects.slice(start, start + PAGE_SIZE);
  }, [sortedProjects, page]);

  const goToNewProject = () => navigate('/student/upload');

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
          My Projects
        </Typography>
        {!loadingMyProjects && !errorMyProjects ? (
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={goToNewProject}
            sx={{ textTransform: 'none', fontWeight: 600, alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Add project
          </Button>
        ) : null}
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          label="Search Projects"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: { sm: 280 } }}
        />
      </Box>

      {loadingMyProjects ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : errorMyProjects ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load projects. Please try again later.
        </Alert>
      ) : myProjects.length === 0 ? (
        <EmptyState
          title="No Projects Yet"
          subtitle="Start by creating your first project"
          actionLabel="Add Project"
          startIcon={<AddIcon />}
          icon={<CreateNewFolderOutlinedIcon aria-hidden />}
          onAction={goToNewProject}
        />
      ) : (
        <>
          <ProjectList
            projects={paginatedProjects}
            showStudentDetails={false}
            showEvaluate={false}
            onProjectUpdate={() => fetchMyProjects()}
            onProjectDelete={() => fetchMyProjects()}
          />
          {sortedProjects.length > PAGE_SIZE ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

export default StudentProjectDashboard;
